package components.project.configuration.profitability

import components.editorNameForText
import components.userName
import components.form.commentSection
import components.project.configuration.profitability.EarningsTableRow.Companion.allRowAccessRights
import it.neckar.commons.kotlin.js.getNotNull
import it.neckar.commons.kotlin.js.safeGet
import it.neckar.editHistory.PositionEditHistory
import it.neckar.lizergy.model.company.CompanyCode
import it.neckar.lizergy.model.company.user.AccessRights.AccessRight.EditEarnings
import it.neckar.lizergy.model.company.user.AccessRights.AccessRight.ViewAccounting
import it.neckar.lizergy.model.configuration.quote.QuoteConfiguration
import it.neckar.lizergy.model.financial.currency.Money
import it.neckar.lizergy.model.income.IncomePercentage
import it.neckar.lizergy.model.income.IncomePercentageCategory
import it.neckar.lizergy.model.income.IncomePercentageCategory.Profit1
import it.neckar.lizergy.model.income.IncomePercentageCategory.Profit2Battery
import it.neckar.lizergy.model.income.IncomePercentageCategory.Profit3Scaffolding
import it.neckar.lizergy.model.income.IncomePercentageCategory.Profit4ElectricityMaterial
import it.neckar.lizergy.model.income.IncomePercentageCategory.Profit5Assembly
import it.neckar.lizergy.model.income.IncomePercentageCategory.Profit6ElectricityWork
import it.neckar.lizergy.model.income.IncomePercentages
import it.neckar.lizergy.model.invoice.InvoiceNumber
import it.neckar.lizergy.model.price.ManualQuoteElements
import it.neckar.lizergy.model.price.ResolvedCompanyEarningsEntry
import it.neckar.lizergy.model.project.ResolvedProject
import it.neckar.open.collections.fastForEach
import it.neckar.open.collections.fastForEachIndexed
import it.neckar.open.formatting.dateFormat
import it.neckar.open.formatting.dateTimeFormat
import it.neckar.open.formatting.format
import it.neckar.open.formatting.formatAsPercentage
import it.neckar.open.kotlin.lang.nullIfBlank
import it.neckar.open.kotlin.lang.percent
import it.neckar.react.common.*
import it.neckar.react.common.form.*
import it.neckar.react.common.form.EditableStatus.Companion.toEditableStatus
import kotlinx.html.InputType
import kotlinx.html.ThScope
import kotlinx.html.js.onClickFunction
import kotlinx.html.title
import plannerI18nConfiguration
import react.*
import react.dom.*
import router.useConfigurationIdFromUrl
import router.useDocumentTitle
import router.useLoadResolvedProjectFromUrl
import services.UiActions
import store.hooks.useLoadProcessStates
import store.hooks.useRequireCompanyForLoggedInUser
import store.hooks.useRequireLoggedInUser
import store.hooks.useSelectCompanyResolver
import store.hooks.useSelectUserResolver
import store.hooks.useSelectUsersAndCompanies

val EarningsOverviewFromUrl: FC<Props> = fc("EarningsOverviewFromUrl") {
  val resolvedProject = useLoadResolvedProjectFromUrl()
  val configurationId = useConfigurationIdFromUrl()
  val companyName = useRequireCompanyForLoggedInUser().simpleName
  val allProcessStatesForComponents = useLoadProcessStates()

  useDocumentTitle(companyName, "Verdienst", allProcessStatesForComponents?.let { resolvedProject?.getDisplayName(it) }, resolvedProject?.get(configurationId)?.configurationName)

  busyIfNull(resolvedProject, allProcessStatesForComponents) { loadedProject, loadedProcessStatesForComponents ->
    EarningsOverview {
      attrs {
        this.project = loadedProject
        this.quoteConfiguration = loadedProject[configurationId]
        this.incomePercentages = IncomePercentages.allWithoutSoftware // ignore Software Role
        this.editableStatus = EditableStatus.Editable
      }
    }
  }
}

val EarningsOverview: FC<EarningsOverviewProps> = fc("EarningsOverview") { props ->
  val loggedInUser = useRequireLoggedInUser()
  val companyResolver = useSelectCompanyResolver()

  val project = props::project.safeGet()
  val quoteConfiguration = props::quoteConfiguration.safeGet()
  val incomePercentages = props::incomePercentages.safeGet()
  val editableStatus = props::editableStatus.safeGet()

  val manualQuoteElements = quoteConfiguration.manualQuoteElements
  val earningsDistribution = quoteConfiguration.earningsDistribution

  val tippgeberCompany = useState(earningsDistribution.tippgeber.company)
  val tippgeberInvoiceNumber = useState(earningsDistribution.tippgeber.invoiceHistory.currentValue?.value)

  val vertriebProjekterfassungCompany = useState(earningsDistribution.vertriebProjekterfassung.company)
  val vertriebProjekterfassungInvoiceNumber = useState(earningsDistribution.vertriebProjekterfassung.invoiceHistory.currentValue?.value)

  val vertriebAngebotsvorstellungCompany = useState(earningsDistribution.vertriebAngebotsvorstellung.company)
  val vertriebAngebotsvorstellungInvoiceNumber = useState(earningsDistribution.vertriebAngebotsvorstellung.invoiceHistory.currentValue?.value)

  val technischePlanungDachCompany = useState(earningsDistribution.technischePlanungDach.company)
  val technischePlanungDachInvoiceNumber = useState(earningsDistribution.technischePlanungDach.invoiceHistory.currentValue?.value)

  val technischePlanungElektrikCompany = useState(earningsDistribution.technischePlanungElektrik.company)
  val technischePlanungElektrikInvoiceNumber = useState(earningsDistribution.technischePlanungElektrik.invoiceHistory.currentValue?.value)

  val montageDachCompany = useState(earningsDistribution.montageDach.company)
  val montageDachInvoiceNumber = useState(earningsDistribution.montageDach.invoiceHistory.currentValue?.value)

  val montageGeruestCompany = useState(earningsDistribution.montageGeruest.company)
  val montageGeruestInvoiceNumber = useState(earningsDistribution.montageGeruest.invoiceHistory.currentValue?.value)

  val elektroInstallationCompany = useState(earningsDistribution.elektroInstallation.company)
  val elektroInstallationInvoiceNumber = useState(earningsDistribution.elektroInstallation.invoiceHistory.currentValue?.value)

  val netzvoranfrageCompany = useState(earningsDistribution.netzvoranfrage.company)
  val netzvoranfrageInvoiceNumber = useState(earningsDistribution.netzvoranfrage.invoiceHistory.currentValue?.value)

  val neckarITInvoiceNumber = useState(earningsDistribution.neckarITAccountingStatus.invoiceHistory.currentValue?.value)

  val tippgeberManualEarnings = useState(earningsDistribution.tippgeber.manualEarnings?.euros)
  val vertriebProjekterfassungManualEarnings = useState(earningsDistribution.vertriebProjekterfassung.manualEarnings?.euros)
  val vertriebAngebotsvorstellungManualEarnings = useState(earningsDistribution.vertriebAngebotsvorstellung.manualEarnings?.euros)
  val technischePlanungDachManualEarnings = useState(earningsDistribution.technischePlanungDach.manualEarnings?.euros)
  val technischePlanungElektrikManualEarnings = useState(earningsDistribution.technischePlanungElektrik.manualEarnings?.euros)
  val montageDachManualEarnings = useState(earningsDistribution.montageDach.manualEarnings?.euros)
  val montageGeruestManualEarnings = useState(earningsDistribution.montageGeruest.manualEarnings?.euros)
  val elektroInstallationManualEarnings = useState(earningsDistribution.elektroInstallation.manualEarnings?.euros)
  val netzvoranfrageManualEarnings = useState(earningsDistribution.netzvoranfrage.manualEarnings?.euros)
  val neckarITManualEarnings = useState(earningsDistribution.neckarITAccountingStatus.manualEarnings?.euros)

  val selectedForAccounting = useState(earningsDistribution.selectedForAccounting)


  val tippgeberToSave = useMemo(
    tippgeberCompany.value,
    tippgeberManualEarnings.value,
    tippgeberInvoiceNumber.value,
  ) {
    if (
      earningsDistribution.tippgeber.company != tippgeberCompany.value ||
      earningsDistribution.tippgeber.manualEarningsHistory.currentValue?.euros != tippgeberManualEarnings.value ||
      earningsDistribution.tippgeber.invoiceHistory.currentValue?.value != tippgeberInvoiceNumber.value
    ) {
      earningsDistribution.tippgeber.copy(
        companyProfile = companyResolver[tippgeberCompany.value].companyProfile,
        manualEarningsHistory = earningsDistribution.tippgeber.manualEarningsHistory.with(tippgeberManualEarnings.value?.let { Money.euros(it) }, loggedInUser.loginName),
        invoiceHistory = earningsDistribution.tippgeber.invoiceHistory.with(tippgeberInvoiceNumber.value?.let { InvoiceNumber(it) }, loggedInUser.loginName),
      )
    } else earningsDistribution.tippgeber
  }


  val vertriebProjekterfassungToSave = useMemo(
    vertriebProjekterfassungCompany.value,
    vertriebProjekterfassungManualEarnings.value,
    vertriebProjekterfassungInvoiceNumber.value,
  ) {
    if (
      earningsDistribution.vertriebProjekterfassung.company != vertriebProjekterfassungCompany.value ||
      earningsDistribution.vertriebProjekterfassung.manualEarningsHistory.currentValue?.euros != vertriebProjekterfassungManualEarnings.value ||
      earningsDistribution.vertriebProjekterfassung.invoiceHistory.currentValue?.value != vertriebProjekterfassungInvoiceNumber.value
    ) {
      earningsDistribution.vertriebProjekterfassung.copy(
        companyProfile = companyResolver[vertriebProjekterfassungCompany.value].companyProfile,
        manualEarningsHistory = earningsDistribution.tippgeber.manualEarningsHistory.with(vertriebProjekterfassungManualEarnings.value?.let { Money.euros(it) }, loggedInUser.loginName),
        invoiceHistory = earningsDistribution.vertriebProjekterfassung.invoiceHistory.with(vertriebProjekterfassungInvoiceNumber.value?.let { InvoiceNumber(it) }, loggedInUser.loginName),
      )
    } else earningsDistribution.vertriebProjekterfassung
  }

  val vertriebAngebotsvorstellungToSave = useMemo(
    vertriebAngebotsvorstellungCompany.value,
    vertriebAngebotsvorstellungManualEarnings.value,
    vertriebAngebotsvorstellungInvoiceNumber.value
  ) {
    if (
      earningsDistribution.vertriebAngebotsvorstellung.company != vertriebAngebotsvorstellungCompany.value ||
      earningsDistribution.vertriebAngebotsvorstellung.manualEarningsHistory.currentValue?.euros != vertriebAngebotsvorstellungManualEarnings.value ||
      earningsDistribution.vertriebAngebotsvorstellung.invoiceHistory.currentValue?.value != vertriebAngebotsvorstellungInvoiceNumber.value
    ) {
      earningsDistribution.vertriebAngebotsvorstellung.copy(
        companyProfile = companyResolver[vertriebAngebotsvorstellungCompany.value].companyProfile,
        manualEarningsHistory = earningsDistribution.tippgeber.manualEarningsHistory.with(vertriebAngebotsvorstellungManualEarnings.value?.let { Money.euros(it) }, loggedInUser.loginName),
        invoiceHistory = earningsDistribution.vertriebAngebotsvorstellung.invoiceHistory.with(vertriebAngebotsvorstellungInvoiceNumber.value?.let { InvoiceNumber(it) }, loggedInUser.loginName),
      )
    } else earningsDistribution.vertriebAngebotsvorstellung
  }

  val technischePlanungDachToSave = useMemo(
    technischePlanungDachCompany.value,
    technischePlanungDachManualEarnings.value,
    technischePlanungDachInvoiceNumber.value
  ) {
    if (
      earningsDistribution.technischePlanungDach.company != technischePlanungDachCompany.value ||
      earningsDistribution.technischePlanungDach.manualEarningsHistory.currentValue?.euros != technischePlanungDachManualEarnings.value ||
      earningsDistribution.technischePlanungDach.invoiceHistory.currentValue?.value != technischePlanungDachInvoiceNumber.value
    ) {
      earningsDistribution.technischePlanungDach.copy(
        companyProfile = companyResolver[technischePlanungDachCompany.value].companyProfile,
        manualEarningsHistory = earningsDistribution.tippgeber.manualEarningsHistory.with(technischePlanungDachManualEarnings.value?.let { Money.euros(it) }, loggedInUser.loginName),
        invoiceHistory = earningsDistribution.technischePlanungDach.invoiceHistory.with(technischePlanungDachInvoiceNumber.value?.let { InvoiceNumber(it) }, loggedInUser.loginName),
      )
    } else earningsDistribution.technischePlanungDach
  }

  val technischePlanungElektrikToSave = useMemo(
    technischePlanungElektrikCompany.value,
    technischePlanungElektrikManualEarnings.value,
    technischePlanungElektrikInvoiceNumber.value
  ) {
    if (
      earningsDistribution.technischePlanungElektrik.company != technischePlanungElektrikCompany.value ||
      earningsDistribution.technischePlanungElektrik.manualEarningsHistory.currentValue?.euros != technischePlanungElektrikManualEarnings.value ||
      earningsDistribution.technischePlanungElektrik.invoiceHistory.currentValue?.value != technischePlanungElektrikInvoiceNumber.value
    ) {
      earningsDistribution.technischePlanungElektrik.copy(
        companyProfile = companyResolver[technischePlanungElektrikCompany.value].companyProfile,
        manualEarningsHistory = earningsDistribution.tippgeber.manualEarningsHistory.with(technischePlanungElektrikManualEarnings.value?.let { Money.euros(it) }, loggedInUser.loginName),
        invoiceHistory = earningsDistribution.technischePlanungElektrik.invoiceHistory.with(technischePlanungElektrikInvoiceNumber.value?.let { InvoiceNumber(it) }, loggedInUser.loginName),
      )
    } else earningsDistribution.technischePlanungElektrik
  }

  val montageDachToSave = useMemo(
    montageDachCompany.value,
    montageDachManualEarnings.value,
    montageDachInvoiceNumber.value
  ) {
    if (
      earningsDistribution.montageDach.company != montageDachCompany.value ||
      earningsDistribution.montageDach.manualEarningsHistory.currentValue?.euros != montageDachManualEarnings.value ||
      earningsDistribution.montageDach.invoiceHistory.currentValue?.value != montageDachInvoiceNumber.value
    ) {
      earningsDistribution.montageDach.copy(
        companyProfile = companyResolver[montageDachCompany.value].companyProfile,
        manualEarningsHistory = earningsDistribution.tippgeber.manualEarningsHistory.with(montageDachManualEarnings.value?.let { Money.euros(it) }, loggedInUser.loginName),
        invoiceHistory = earningsDistribution.montageDach.invoiceHistory.with(montageDachInvoiceNumber.value?.let { InvoiceNumber(it) }, loggedInUser.loginName),
      )
    } else earningsDistribution.montageDach
  }

  val montageGeruestToSave = useMemo(
    montageGeruestCompany.value,
    montageGeruestManualEarnings.value,
    montageGeruestInvoiceNumber.value
  ) {
    if (
      earningsDistribution.montageGeruest.company != montageGeruestCompany.value ||
      earningsDistribution.montageGeruest.manualEarningsHistory.currentValue?.euros != montageGeruestManualEarnings.value ||
      earningsDistribution.montageGeruest.invoiceHistory.currentValue?.value != montageGeruestInvoiceNumber.value
    ) {
      earningsDistribution.montageGeruest.copy(
        companyProfile = companyResolver[montageGeruestCompany.value].companyProfile,
        manualEarningsHistory = earningsDistribution.tippgeber.manualEarningsHistory.with(montageGeruestManualEarnings.value?.let { Money.euros(it) }, loggedInUser.loginName),
        invoiceHistory = earningsDistribution.montageGeruest.invoiceHistory.with(montageGeruestInvoiceNumber.value?.let { InvoiceNumber(it) }, loggedInUser.loginName),
      )
    } else earningsDistribution.montageGeruest
  }

  val elektroInstallationToSave = useMemo(
    elektroInstallationCompany.value,
    elektroInstallationManualEarnings.value,
    elektroInstallationInvoiceNumber.value
  ) {
    if (
      earningsDistribution.elektroInstallation.company != elektroInstallationCompany.value ||
      earningsDistribution.elektroInstallation.manualEarningsHistory.currentValue?.euros != elektroInstallationManualEarnings.value ||
      earningsDistribution.elektroInstallation.invoiceHistory.currentValue?.value != elektroInstallationInvoiceNumber.value
    ) {
      earningsDistribution.elektroInstallation.copy(
        companyProfile = companyResolver[elektroInstallationCompany.value].companyProfile,
        manualEarningsHistory = earningsDistribution.tippgeber.manualEarningsHistory.with(elektroInstallationManualEarnings.value?.let { Money.euros(it) }, loggedInUser.loginName),
        invoiceHistory = earningsDistribution.elektroInstallation.invoiceHistory.with(elektroInstallationInvoiceNumber.value?.let { InvoiceNumber(it) }, loggedInUser.loginName),
      )
    } else earningsDistribution.elektroInstallation
  }

  val netzvoranfrageToSave = useMemo(
    netzvoranfrageCompany.value,
    netzvoranfrageManualEarnings.value,
    netzvoranfrageInvoiceNumber.value
  ) {
    if (
      earningsDistribution.netzvoranfrage.company != netzvoranfrageCompany.value ||
      earningsDistribution.netzvoranfrage.manualEarningsHistory.currentValue?.euros != netzvoranfrageManualEarnings.value ||
      earningsDistribution.netzvoranfrage.invoiceHistory.currentValue?.value != netzvoranfrageInvoiceNumber.value
    ) {
      earningsDistribution.netzvoranfrage.copy(
        companyProfile = companyResolver[netzvoranfrageCompany.value].companyProfile,
        manualEarningsHistory = earningsDistribution.tippgeber.manualEarningsHistory.with(netzvoranfrageManualEarnings.value?.let { Money.euros(it) }, loggedInUser.loginName),
        invoiceHistory = earningsDistribution.netzvoranfrage.invoiceHistory.with(netzvoranfrageInvoiceNumber.value?.let { InvoiceNumber(it) }, loggedInUser.loginName),
      )
    } else earningsDistribution.netzvoranfrage
  }

  val neckarITToSave = useMemo(
    neckarITManualEarnings.value,
    neckarITInvoiceNumber.value
  ) {
    if (earningsDistribution.neckarITAccountingStatus.manualEarningsHistory.currentValue?.euros != neckarITManualEarnings.value || earningsDistribution.neckarITAccountingStatus.invoiceHistory.currentValue?.value != neckarITInvoiceNumber.value?.nullIfBlank()) {
      earningsDistribution.neckarITAccountingStatus.copy(
        manualEarningsHistory = earningsDistribution.tippgeber.manualEarningsHistory.with(neckarITManualEarnings.value?.let { Money.euros(it) }, loggedInUser.loginName),
        invoiceHistory = earningsDistribution.neckarITAccountingStatus.invoiceHistory.with(neckarITInvoiceNumber.value?.nullIfBlank()?.let { InvoiceNumber(it) }, loggedInUser.loginName),
      )
    } else earningsDistribution.neckarITAccountingStatus
  }


  val updatedEarningsDistribution = useMemo(
    tippgeberToSave,
    vertriebProjekterfassungToSave,
    vertriebAngebotsvorstellungToSave,
    technischePlanungDachToSave,
    technischePlanungElektrikToSave,
    montageDachToSave,
    montageGeruestToSave,
    elektroInstallationToSave,
    netzvoranfrageToSave,
    neckarITToSave,
    selectedForAccounting.value,
  ) {
    when (editableStatus) {
      EditableStatus.Editable -> {}
      EditableStatus.ReadOnly -> return@useMemo earningsDistribution
    }
    earningsDistribution.copy(
      tippgeber = tippgeberToSave,
      vertriebProjekterfassung = vertriebProjekterfassungToSave,
      vertriebAngebotsvorstellung = vertriebAngebotsvorstellungToSave,
      technischePlanungDach = technischePlanungDachToSave,
      technischePlanungElektrik = technischePlanungElektrikToSave,
      montageDach = montageDachToSave,
      montageGeruest = montageGeruestToSave,
      elektroInstallation = elektroInstallationToSave,
      netzvoranfrage = netzvoranfrageToSave,
      neckarITAccountingStatus = neckarITToSave,
      selectedForAccounting = selectedForAccounting.value,
    ).also { newEarningsDistribution ->
      if (newEarningsDistribution != earningsDistribution) {
        UiActions.saveEarningsDistribution(
          projectId = project.projectId,
          configurationId = quoteConfiguration.configurationId,
          earningsDistribution = newEarningsDistribution,
        )
      }
    }
  }

  val earningElements: List<EarningsTableRow> = useMemo(incomePercentages, updatedEarningsDistribution) {
    incomePercentages.mapIndexed { index, incomePercentage ->
      val resolvedCompanyEarningsEntry = updatedEarningsDistribution.all[index]
      EarningsTableRow(
        label = incomePercentage.description,
        earningsCompanyEntry = resolvedCompanyEarningsEntry,
        accessRight = allRowAccessRights[index]
      )
    }
  }

  val manualProfit1 = useState(manualQuoteElements.manualSumsForTags[Profit1]?.currentValue?.euros)
  val manualProfit2Battery = useState(manualQuoteElements.manualSumsForTags[Profit2Battery]?.currentValue?.euros)
  val manualProfit3Scaffolding = useState(manualQuoteElements.manualSumsForTags[Profit3Scaffolding]?.currentValue?.euros)
  val manualProfit4ElectricityMaterial = useState(manualQuoteElements.manualSumsForTags[Profit4ElectricityMaterial]?.currentValue?.euros)
  val manualProfit5Assembly = useState(manualQuoteElements.manualSumsForTags[Profit5Assembly]?.currentValue?.euros)
  val manualProfit6ElectricityWork = useState(manualQuoteElements.manualSumsForTags[Profit6ElectricityWork]?.currentValue?.euros)

  val manualQuoteElementsMap = buildMap {
    put(Profit1, IncomePercentageCategoryAndState(Profit1, manualProfit1))
    put(Profit2Battery, IncomePercentageCategoryAndState(Profit2Battery, manualProfit2Battery))
    put(Profit3Scaffolding, IncomePercentageCategoryAndState(Profit3Scaffolding, manualProfit3Scaffolding))
    put(Profit4ElectricityMaterial, IncomePercentageCategoryAndState(Profit4ElectricityMaterial, manualProfit4ElectricityMaterial))
    put(Profit5Assembly, IncomePercentageCategoryAndState(Profit5Assembly, manualProfit5Assembly))
    put(Profit6ElectricityWork, IncomePercentageCategoryAndState(Profit6ElectricityWork, manualProfit6ElectricityWork))
  }

  val updatedManualQuoteElements = useMemo(
    manualProfit1.value,
    manualProfit2Battery.value,
    manualProfit3Scaffolding.value,
    manualProfit4ElectricityMaterial.value,
    manualProfit5Assembly.value,
    manualProfit6ElectricityWork.value,
  ) {
    when (editableStatus) {
      EditableStatus.Editable -> {}
      EditableStatus.ReadOnly -> return@useMemo manualQuoteElements
    }
    manualQuoteElements.copy(
      manualSumsForTags = buildMap {
        IncomePercentageCategory.entries.fastForEach { incomePercentageCategory ->
          val newManualSum = manualQuoteElementsMap[incomePercentageCategory]?.incomeState?.value?.let { Money.euros(it) }
          val oldEditHistory = manualQuoteElements.manualSumsForTags[incomePercentageCategory]
          if (oldEditHistory != null) {
            val oldManualSum = oldEditHistory.currentValue
            val updatedEditHistory = if (newManualSum != oldManualSum) oldEditHistory.with(newManualSum, loggedInUser.loginName) else oldEditHistory
            put(incomePercentageCategory, updatedEditHistory)
          } else if (newManualSum != null) {
            val newEditHistory = PositionEditHistory<Money?>(newManualSum, loggedInUser.loginName)
            put(incomePercentageCategory, newEditHistory)
          }
        }
      },
    ).also { updatedManualQuoteElements ->
      if (updatedManualQuoteElements != manualQuoteElements) {
        UiActions.saveManualQuoteElements(
          projectId = project.projectId,
          configurationId = quoteConfiguration.configurationId,
          manualQuoteElements = updatedManualQuoteElements,
        )
      }
    }
  }

  val updatedConfiguration = useMemo(updatedEarningsDistribution, updatedManualQuoteElements) {
    quoteConfiguration.copy(
      earningsDistribution = updatedEarningsDistribution,
      manualQuoteElements = updatedManualQuoteElements,
    )
  }


  div("my-3") {
    commentSection(updatedEarningsDistribution)
  }

  EarningsTable {
    attrs {
      this.configuration = updatedConfiguration

      this.earningsTableRows = earningElements

      this.tippgeberCompany = tippgeberCompany
      this.tippgeberInvoiceNumber = tippgeberInvoiceNumber
      this.tippgeberManualEarnings = tippgeberManualEarnings

      this.vertriebProjekterfassungCompany = vertriebProjekterfassungCompany
      this.vertriebProjekterfassungInvoiceNumber = vertriebProjekterfassungInvoiceNumber
      this.vertriebProjekterfassungManualEarnings = vertriebProjekterfassungManualEarnings

      this.vertriebAngebotsvorstellungCompany = vertriebAngebotsvorstellungCompany
      this.vertriebAngebotsvorstellungInvoiceNumber = vertriebAngebotsvorstellungInvoiceNumber
      this.vertriebAngebotsvorstellungManualEarnings = vertriebAngebotsvorstellungManualEarnings

      this.technischePlanungDachCompany = technischePlanungDachCompany
      this.technischePlanungDachInvoiceNumber = technischePlanungDachInvoiceNumber
      this.technischePlanungDachManualEarnings = technischePlanungDachManualEarnings

      this.technischePlanungElektrikCompany = technischePlanungElektrikCompany
      this.technischePlanungElektrikInvoiceNumber = technischePlanungElektrikInvoiceNumber
      this.technischePlanungElektrikManualEarnings = technischePlanungElektrikManualEarnings

      this.montageDachCompany = montageDachCompany
      this.montageDachInvoiceNumber = montageDachInvoiceNumber
      this.montageDachManualEarnings = montageDachManualEarnings

      this.montageGeruestCompany = montageGeruestCompany
      this.montageGeruestInvoiceNumber = montageGeruestInvoiceNumber
      this.montageGeruestManualEarnings = montageGeruestManualEarnings

      this.elektroInstallationCompany = elektroInstallationCompany
      this.elektroInstallationInvoiceNumber = elektroInstallationInvoiceNumber
      this.elektroInstallationManualEarnings = elektroInstallationManualEarnings

      this.netzvoranfrageCompany = netzvoranfrageCompany
      this.netzvoranfrageInvoiceNumber = netzvoranfrageInvoiceNumber
      this.netzvoranfrageManualEarnings = netzvoranfrageManualEarnings

      this.neckarITInvoiceNumber = neckarITInvoiceNumber
      this.neckarITManualEarnings = neckarITManualEarnings

      this.manualQuoteElementsMap = manualQuoteElementsMap
      this.updatedManualQuoteElements = updatedManualQuoteElements

      this.forMainCompany = project.sellingCompany
      this.editableStatus = editableStatus
    }
  }

  div("my-3") {
    if (selectedForAccounting.value.not()) {
      button(classes = "btn btn-outline-primary w-100") {
        attrs {
          disabled = editableStatus == EditableStatus.ReadOnly
          onClickFunction = { selectedForAccounting.setter(true) }
        }
        +"Projekt buchen"
      }
    } else {
      button(classes = "btn btn-outline-danger w-100") {
        attrs {
          disabled = editableStatus == EditableStatus.ReadOnly
          onClickFunction = { selectedForAccounting.setter(false) }
        }
        +"Projekt aus Buchung entfernen"
      }

    }
  }

}

/**
 * Renders a table that displays the profit for each of the roles
 */
val EarningsTable: FC<EarningsTableProps> = fc("EarningsTable") { props ->
  // Get the logged-in user to check access rights
  val loggedInUser = useRequireLoggedInUser()
  val userResolver = useSelectUserResolver()

  val configuration = props::configuration.safeGet()
  val quoteElements = configuration.getQuoteElements()

  // Each entry represents the income percentages for a single role
  val earningsTableRows = props::earningsTableRows.safeGet()

  val tippgeberCompany = props::tippgeberCompany.getNotNull()
  val tippgeberInvoiceNumber = props::tippgeberInvoiceNumber.getNotNull()
  val tippgeberManualEarnings = props::tippgeberManualEarnings.getNotNull()

  val vertriebProjekterfassungCompany = props::vertriebProjekterfassungCompany.getNotNull()
  val vertriebProjekterfassungInvoiceNumber = props::vertriebProjekterfassungInvoiceNumber.getNotNull()
  val vertriebProjekterfassungManualEarnings = props::vertriebProjekterfassungManualEarnings.getNotNull()

  val vertriebAngebotsvorstellungCompany = props::vertriebAngebotsvorstellungCompany.getNotNull()
  val vertriebAngebotsvorstellungInvoiceNumber = props::vertriebAngebotsvorstellungInvoiceNumber.getNotNull()
  val vertriebAngebotsvorstellungManualEarnings = props::vertriebAngebotsvorstellungManualEarnings.getNotNull()

  val technischePlanungDachCompany = props::technischePlanungDachCompany.getNotNull()
  val technischePlanungDachInvoiceNumber = props::technischePlanungDachInvoiceNumber.getNotNull()
  val technischePlanungDachManualEarnings = props::technischePlanungDachManualEarnings.getNotNull()

  val technischePlanungElektrikCompany = props::technischePlanungElektrikCompany.getNotNull()
  val technischePlanungElektrikInvoiceNumber = props::technischePlanungElektrikInvoiceNumber.getNotNull()
  val technischePlanungElektrikManualEarnings = props::technischePlanungElektrikManualEarnings.getNotNull()

  val montageDachCompany = props::montageDachCompany.getNotNull()
  val montageDachInvoiceNumber = props::montageDachInvoiceNumber.getNotNull()
  val montageDachManualEarnings = props::montageDachManualEarnings.getNotNull()

  val montageGeruestCompany = props::montageGeruestCompany.getNotNull()
  val montageGeruestInvoiceNumber = props::montageGeruestInvoiceNumber.getNotNull()
  val montageGeruestManualEarnings = props::montageGeruestManualEarnings.getNotNull()

  val elektroInstallationCompany = props::elektroInstallationCompany.getNotNull()
  val elektroInstallationInvoiceNumber = props::elektroInstallationInvoiceNumber.getNotNull()
  val elektroInstallationManualEarnings = props::elektroInstallationManualEarnings.getNotNull()

  val netzvoranfrageCompany = props::netzvoranfrageCompany.getNotNull()
  val netzvoranfrageInvoiceNumber = props::netzvoranfrageInvoiceNumber.getNotNull()
  val netzvoranfrageManualEarnings = props::netzvoranfrageManualEarnings.getNotNull()

  val neckarITInvoiceNumber = props::neckarITInvoiceNumber.getNotNull()
  val neckarITManualEarnings = props::neckarITManualEarnings.getNotNull()

  val manualQuoteElementsMap = props::manualQuoteElementsMap.safeGet()
  val updatedManualQuoteElements = props::updatedManualQuoteElements.safeGet()

  val forMainCompany = props::forMainCompany.safeGet()
  val editableStatus = props::editableStatus.safeGet()

  fun companyStateInstanceForIndex(index: Int): StateInstance<CompanyCode> {
    return when (index) {
      0 -> tippgeberCompany
      1 -> vertriebProjekterfassungCompany
      2 -> vertriebAngebotsvorstellungCompany
      3 -> technischePlanungDachCompany
      4 -> technischePlanungElektrikCompany
      5 -> montageDachCompany
      6 -> montageGeruestCompany
      7 -> elektroInstallationCompany
      8 -> netzvoranfrageCompany
      else -> throw IllegalArgumentException("No company state instance for index $index")
    }
  }

  fun manualEarningsInstanceForIndex(index: Int): StateInstance<Double?> {
    return when (index) {
      0 -> tippgeberManualEarnings
      1 -> vertriebProjekterfassungManualEarnings
      2 -> vertriebAngebotsvorstellungManualEarnings
      3 -> technischePlanungDachManualEarnings
      4 -> technischePlanungElektrikManualEarnings
      5 -> montageDachManualEarnings
      6 -> montageGeruestManualEarnings
      7 -> elektroInstallationManualEarnings
      8 -> netzvoranfrageManualEarnings
      else -> throw IllegalArgumentException("No manual earnings state instance for index $index")
    }
  }

  fun accountingStateInstanceForIndex(index: Int): StateInstance<String?> {
    return when (index) {
      0 -> tippgeberInvoiceNumber
      1 -> vertriebProjekterfassungInvoiceNumber
      2 -> vertriebAngebotsvorstellungInvoiceNumber
      3 -> technischePlanungDachInvoiceNumber
      4 -> technischePlanungElektrikInvoiceNumber
      5 -> montageDachInvoiceNumber
      6 -> montageGeruestInvoiceNumber
      7 -> elektroInstallationInvoiceNumber
      8 -> netzvoranfrageInvoiceNumber
      else -> throw IllegalArgumentException("No accounting state instance for index $index")
    }
  }


  table("table table-responsive") {
    thead {
      tr {
        th(ThScope.col, "text-start justify-content-between") {
          +"Rolle"
        }
        if (loggedInUser.accessRights.canAccess(ViewAccounting)) {
          th(ThScope.col, "text-start justify-content-between") {
            +"Rechnungsnummer"
          }
          th(ThScope.col, "text-start justify-content-between") {
            +"Verdienst"
          }
        }
      }
    }
    tbody {
      /**
       * if the user cannot access the role of the earning
       * element don't display the line
       */

      earningsTableRows.fastForEachIndexed { index, earningsTableRow ->
        if (loggedInUser.accessRights.canAccess(earningsTableRow.accessRight)) {
          EarningsTableLineWithCompanyEdit {
            attrs {
              this.configuration = configuration
              this.label = earningsTableRow.label
              this.responsibleCompanyState = companyStateInstanceForIndex(index)
              this.invoiceNumberState = accountingStateInstanceForIndex(index)
              this.manualProfitState = manualEarningsInstanceForIndex(index)
              this.earningsCompanyEntry = earningsTableRow.earningsCompanyEntry
              this.forMainCompany = forMainCompany
              this.editableStatus = editableStatus
            }
          }
        }
      }

      tr {

        td("text-start my-auto") {
          b { +"Neckar IT" }
        }

        EarningsTableLine {
          attrs {
            this.configuration = configuration
            this.forCompany = CompanyCode.NeckarIT
            this.invoiceNumberState = neckarITInvoiceNumber
            this.manualProfitState = neckarITManualEarnings
            this.incomePercentage = configuration.softwareIncomePercentage
            this.invoiceHistory = configuration.earningsDistribution.neckarITAccountingStatus.invoiceHistory
            this.manualEarningsHistory = configuration.earningsDistribution.neckarITAccountingStatus.manualEarningsHistory
            this.editableStatus = editableStatus
          }
        }

      }

      manualQuoteElementsMap.values.toList().fastForEach { categoryAndState ->
        val incomePercentageCategory = categoryAndState.incomePercentageCategory
        val state = categoryAndState.incomeState
        val netPriceForCategory = quoteElements.sumsForTags[incomePercentageCategory]

        tr {
          td("form-text my-auto") {
            attrs {
              colSpan = "2"
            }
            +incomePercentageCategory.description
          }
          td("text-end") {
            val id = UniqueId.create("${configuration.uuid}-${incomePercentageCategory.name}")
            euroInputField(state = state, fieldName = id, netPriceForCategory = netPriceForCategory, editableStatus = editableStatus)
            updatedManualQuoteElements.manualSumsForTags[incomePercentageCategory]?.let { editHistoryEntry ->
              div("form-text") {
                userName(editHistoryEntry.currentEdit.loginName, userResolver)
                +": "
                +dateFormat.format(editHistoryEntry.lastEditedTime, plannerI18nConfiguration)
              }
            }
          }
        }
      }

      tr {
        td {
          attrs {
            colSpan = "2"
          }
          if (quoteElements.discountPercentage != 0.0.percent) {
            div("col my-auto") { b { +"Rechnungssumme" } }
            div("col my-auto") { +"Rabatt (${quoteElements.discountPercentage.formatAsPercentage(0)})" }
          }
          div("col my-auto") { b { +"Nettosumme" } }
        }
        td {
          if (quoteElements.discountPercentage != 0.0.percent) {
            div("col text-end") { b { +quoteElements.total.format(plannerI18nConfiguration) } }
            div("col text-end") { +quoteElements.discount.format(plannerI18nConfiguration) }
          }
          div("col text-end") { b { +quoteElements.net.format(plannerI18nConfiguration) } }
        }

      }

    }

  }

}

/**
 * Single row of the Table for one Role
 */
val EarningsTableLineWithCompanyEdit: FC<EarningsTableLineWithCompanyEditProps> = fc("EarningsTableLineWithCompanyEdit") { props ->
  val loggedInUser = useRequireLoggedInUser()
  val loggedInCompany = useRequireCompanyForLoggedInUser()
  val userAndCompanies = useSelectUsersAndCompanies()

  val configuration = props::configuration.safeGet()
  val label = props::label.safeGet()
  val responsibleCompanyState = props::responsibleCompanyState.getNotNull()
  val invoiceNumberState = props::invoiceNumberState.getNotNull()
  val manualProfitState = props::manualProfitState.getNotNull()
  val earningsCompanyEntry = props::earningsCompanyEntry.safeGet()
  val forMainCompany = props::forMainCompany.safeGet()
  val editableStatus = props::editableStatus.safeGet()

  val allCompanies = userAndCompanies.activeCompanies(loggedInUser)
  val multipleCompaniesLoaded = allCompanies.size > 1

  val allCompaniesByCode = allCompanies.associateBy { it.companyCode }

  val canEdit = loggedInUser.accessRights.canAccess(EditEarnings).toEditableStatus().and(editableStatus)


  tr {

    td("text-start") {
      if (multipleCompaniesLoaded) {
        floatingSelect(
          valueAndSetter = responsibleCompanyState,
          idProvider = { it.value },
          formatter = { allCompaniesByCode[it]?.name ?: it.value },
          availableOptions = allCompanies.map { it.companyCode },
          fieldName = "companyFilter$label",
          title = label,
          editableStatus = canEdit,
        )
      } else {
        checkbox(
          value = responsibleCompanyState.value == loggedInCompany.companyCode,
          onChange = { responsibleCompanyState.setter(if (it) loggedInCompany.companyCode else forMainCompany) },
          fieldName = label,
          title = label,
          editableStatus = canEdit.and(invoiceNumberState.value == null),
        )
      }
    }

    if (earningsCompanyEntry.company != forMainCompany && loggedInUser.accessRights.canAccess(ViewAccounting)) {
      EarningsTableLine {
        attrs {
          this.configuration = configuration
          this.forCompany = responsibleCompanyState.value
          this.invoiceNumberState = invoiceNumberState
          this.manualProfitState = manualProfitState
          this.incomePercentage = earningsCompanyEntry.incomePercentage
          this.invoiceHistory = earningsCompanyEntry.invoiceHistory
          this.manualEarningsHistory = earningsCompanyEntry.manualEarningsHistory
          this.editableStatus = editableStatus
        }
      }
    } else {
      td {}
      td {}
    }

  }

}

val EarningsTableLine: FC<EarningsTableLineProps> = fc("EarningsTableLine") { props ->
  val userResolver = useSelectUserResolver()

  val configuration = props::configuration.safeGet()
  val forCompany = props::forCompany.getNotNull()
  val invoiceNumberState = props::invoiceNumberState.getNotNull()
  val manualProfitState = props::manualProfitState.getNotNull()
  val incomePercentage = props::incomePercentage.safeGet()
  val invoiceHistory = props::invoiceHistory.safeGet()
  val manualEarningsHistory = props::manualEarningsHistory.safeGet()
  val editableStatus = props::editableStatus.safeGet()


  td {
    val id = UniqueId.create("${configuration.uuid}-${forCompany}")
    nullableInputField(
      value = invoiceNumberState.value,
      onChange = { invoiceNumberState.setter(it?.nullIfBlank()) },
      fieldName = id,
      title = "Rechnungsnummer ${incomePercentage.description}",
      editableStatus = editableStatus,
    )
    span("form-text ms-3") {
      val currentEdit = invoiceHistory.currentEdit
      attrs {
        this.title = buildString {
          invoiceHistory.allEdits.fastForEach {
            val invoiceNumber = it.value?.value ?: "-"
            val editor = it.loginName?.let { userResolver[it].editorNameForText() } ?: "-"
            val editTime = dateFormat.format(it.editTime, plannerI18nConfiguration)
            appendLine("$editTime: $editor")
            appendLine(invoiceNumber)
            appendLine()
          }
        }
      }
      userName(currentEdit.loginName, userResolver)
      +": "
      if (currentEdit.value != null) {
        +dateTimeFormat.format(invoiceHistory.lastEditedTime, plannerI18nConfiguration)
      } else {
        +dateFormat.format(invoiceHistory.lastEditedTime, plannerI18nConfiguration)
      }
    }
  }

  td {
    val relevantEarnings = configuration.getEarningsForIncomePercentage(incomePercentage)
    val id = UniqueId.create("${forCompany}-${incomePercentage.description}")
    euroInputField(state = manualProfitState, fieldName = id, netPriceForCategory = relevantEarnings, editableStatus = editableStatus)
    div("form-text") {
      userName(manualEarningsHistory.currentEdit.loginName, userResolver)
      +": "
      +dateFormat.format(manualEarningsHistory.lastEditedTime, plannerI18nConfiguration)
    }
  }
}

private fun RDOMBuilder<*>.euroInputField(
  state: StateInstance<Double?>,
  fieldName: String,
  netPriceForCategory: Money?,
  mergeAbove: Boolean = false,
  mergeBelow: Boolean = false,
  editableStatus: EditableStatus,
) {
  div("input-group") {
    nullableInputField(
      value = state.value?.toString(),
      onChange = { state.setter(it?.toDoubleOrNull()) },
      fieldName = fieldName,
      title = netPriceForCategory?.format(plannerI18nConfiguration) ?: "Nettopreis",
      placeholder = (netPriceForCategory?.euros ?: 0.00).format(i18nConfiguration = plannerI18nConfiguration),
      classes = "form-control float-end text-end",
      editableStatus = editableStatus,
    ) {
      attrs {
        type = InputType.number
        if (mergeAbove) mergedAbove()
        if (mergeBelow) mergedBelow()
      }
    }
    div("input-group-append") {
      span("input-group-text") { +"€" }
    }
  }
}

data class IncomePercentageCategoryAndState(
  val incomePercentageCategory: IncomePercentageCategory,
  val incomeState: StateInstance<Double?>,
)

external interface EarningsOverviewProps : Props {
  var project: ResolvedProject
  var quoteConfiguration: QuoteConfiguration
  var incomePercentages: List<IncomePercentage>
  var editableStatus: EditableStatus
}

external interface EarningsTableProps : Props {
  var configuration: QuoteConfiguration

  var earningsTableRows: List<EarningsTableRow>

  var tippgeberCompany: StateInstance<CompanyCode>
  var tippgeberInvoiceNumber: StateInstance<String?>
  var tippgeberManualEarnings: StateInstance<Double?>

  var vertriebProjekterfassungCompany: StateInstance<CompanyCode>
  var vertriebProjekterfassungInvoiceNumber: StateInstance<String?>
  var vertriebProjekterfassungManualEarnings: StateInstance<Double?>

  var vertriebAngebotsvorstellungCompany: StateInstance<CompanyCode>
  var vertriebAngebotsvorstellungInvoiceNumber: StateInstance<String?>
  var vertriebAngebotsvorstellungManualEarnings: StateInstance<Double?>

  var technischePlanungDachCompany: StateInstance<CompanyCode>
  var technischePlanungDachInvoiceNumber: StateInstance<String?>
  var technischePlanungDachManualEarnings: StateInstance<Double?>

  var technischePlanungElektrikCompany: StateInstance<CompanyCode>
  var technischePlanungElektrikInvoiceNumber: StateInstance<String?>
  var technischePlanungElektrikManualEarnings: StateInstance<Double?>

  var montageDachCompany: StateInstance<CompanyCode>
  var montageDachInvoiceNumber: StateInstance<String?>
  var montageDachManualEarnings: StateInstance<Double?>

  var montageGeruestCompany: StateInstance<CompanyCode>
  var montageGeruestInvoiceNumber: StateInstance<String?>
  var montageGeruestManualEarnings: StateInstance<Double?>

  var elektroInstallationCompany: StateInstance<CompanyCode>
  var elektroInstallationInvoiceNumber: StateInstance<String?>
  var elektroInstallationManualEarnings: StateInstance<Double?>

  var netzvoranfrageCompany: StateInstance<CompanyCode>
  var netzvoranfrageInvoiceNumber: StateInstance<String?>
  var netzvoranfrageManualEarnings: StateInstance<Double?>

  var neckarITInvoiceNumber: StateInstance<String?>
  var neckarITManualEarnings: StateInstance<Double?>

  var manualQuoteElementsMap: Map<IncomePercentageCategory, IncomePercentageCategoryAndState>
  var updatedManualQuoteElements: ManualQuoteElements

  var forMainCompany: CompanyCode
  var editableStatus: EditableStatus
}

external interface EarningsTableLineWithCompanyEditProps : Props {
  var configuration: QuoteConfiguration
  var label: String
  var responsibleCompanyState: StateInstance<CompanyCode>
  var invoiceNumberState: StateInstance<String?>
  var manualProfitState: StateInstance<Double?>
  var earningsCompanyEntry: ResolvedCompanyEarningsEntry
  var forMainCompany: CompanyCode
  var editableStatus: EditableStatus
}

external interface EarningsTableLineProps : Props {
  var configuration: QuoteConfiguration
  var forCompany: CompanyCode
  var invoiceNumberState: StateInstance<String?>
  var manualProfitState: StateInstance<Double?>
  var incomePercentage: IncomePercentage
  var invoiceHistory: PositionEditHistory<InvoiceNumber?>
  var manualEarningsHistory: PositionEditHistory<Money?>
  var editableStatus: EditableStatus
}
